import {
  Outlet,
  createFileRoute,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect } from "react";
import {
  authGate,
  useAuthStore,
  useAuthLoading,
  useIsAuthenticated,
  useCurrentTenant,
} from "../stores/auth-store.js";
import { DesktopShell } from "../components/desktop/desktop-shell.js";

/**
 * `_authenticated` layout — wraps every protected route in the DesktopShell.
 *
 * The `_` prefix is TanStack Router's "pathless route group" convention:
 * it groups child routes under this auth gate without adding a URL segment.
 *
 * Auth strategy (one rule for both, `authGate` in the auth store):
 *   1. `beforeLoad` synchronously inspects the Zustand store. If we know
 *      the user is unauthenticated and not still loading, redirect to /login.
 *   2. `component` fires `checkAuth()` on first mount while showing a spinner.
 *   3. A `useEffect` watches the resolved auth state and redirects to /login
 *      once the API has answered 401/403. While the API can't be reached
 *      (a restart on a deploy) the user stays put and the root layout shows
 *      a "Reconnecting…" notice.
 */
export const Route = createFileRoute("/_authenticated")({
  beforeLoad: () => {
    if (authGate(useAuthStore.getState()) === "login") {
      throw redirect({ to: "/login" });
    }
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const navigate = useNavigate();
  const checkAuth = useAuthStore((s) => s.checkAuth);
  const isLoading = useAuthLoading();
  const isAuthenticated = useIsAuthenticated();
  const tenant = useCurrentTenant();
  const gate = authGate({ isLoading, isAuthenticated, tenant });

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (gate === "login") {
      void navigate({ to: "/login" });
    } else if (gate === "welcome") {
      void navigate({ to: "/welcome" });
    }
  }, [gate, navigate]);

  if (gate === "wait") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-canvas)]">
        <div
          role="status"
          aria-label="Loading"
          className="size-8 animate-spin rounded-full border-2 border-[var(--color-border-default)] border-t-[var(--color-accent-amber)]"
        />
      </div>
    );
  }

  if (gate !== "show") {
    return null;
  }

  return (
    <DesktopShell>
      <Outlet />
    </DesktopShell>
  );
}
